import React from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import { useFormatMessage } from 'src/providers/intl'

// Constants
import { QUESTIONS } from 'src/constants/messages'
import { AVG_SELLING_FEELS } from 'src/constants/shouldYouSell'
import { HOMEOWNER_CASH_POSITION_TOPIC_KEY } from 'src/constants/topicKeys'

// Selectors
import {
  selectEquityValue,
  selectRecentSoldDate,
} from 'src/store/selectors/shouldYouSell'
import { selectHomeValue } from 'src/store/selectors/home/metadata'

// Components
import QuestionSingle from 'src/Components/shared/Questions/QuestionSingleContainer'
import { Text, If, Unless } from 'src/Components'
import Format from 'src/Components/shared/Format'
import { IconMinus } from 'src/Components/shared/Icon'
import SoldDateDisclaimer from '../SoldDateDisclaimer'

// Styles
import styles from './Body.module.scss'

const Body = () => {
  const intlFormat = useFormatMessage()

  const homeValue = useSelector(selectHomeValue)
  const equity = useSelector(selectEquityValue)
  const recentSoldDate = useSelector(selectRecentSoldDate)

  const roundedEquity = Math.round(equity)
  const roundedSellingFees = Math.round(homeValue * AVG_SELLING_FEELS)
  const cashRemaining = roundedEquity - roundedSellingFees
  const cashRemainingVariant = cashRemaining <= 0 ? 'danger' : 'warning'

  return (
    <div>
      <Text
        className={styles.heading}
        element='div'
        variant='neutralCool'
        variantCount='900'
      >
        <If condition={cashRemaining <= 0}>
          <FormattedMessage
            id='ShouldYouSell.cashPosition.bad.body.headerNeg'
            defaultMessage='If you sold today for {homeValue}, you might need cash for selling fees'
            values={{
              homeValue: (
                <Text
                  element='span'
                  weight='bold'
                  variant='neutralCool'
                  variantCount='900'
                >
                  <Format.Currency
                    className={styles.homeValue}
                    abbreviate
                    value={homeValue}
                  />
                </Text>
              ),
            }}
          />
        </If>
        <Unless condition={cashRemaining <= 0}>
          <FormattedMessage
            id='ShouldYouSell.cashPosition.bad.body.headerPositive'
            defaultMessage='If you sold today for {homeValue}, you may not have enough cash for a new down payment'
            values={{
              homeValue: (
                <Text
                  element='span'
                  weight='bold'
                  variant='neutralCool'
                  variantCount='900'
                >
                  <Format.Currency
                    className={styles.homeValue}
                    abbreviate
                    value={homeValue}
                  />
                </Text>
              ),
            }}
          />
        </Unless>
      </Text>
      <div className={styles.calculation}>
        <div className={styles.row}>
          <div className={classnames(styles.column, styles.column1)} />
          <div className={classnames(styles.column, styles.column2)}>
            <Text element='span' variant='primary'>
              <Format.Currency value={roundedEquity} />
            </Text>
          </div>
          <div className={classnames(styles.column, styles.column3)}>
            <Text variant='neutralCool' variantCount='700'>
              <FormattedMessage
                id='ShouldYouSell.cashPosition.bad.body.equity'
                defaultMessage='Estimated equity'
              />
            </Text>
          </div>
        </div>
        <div className={styles.row}>
          <div className={classnames(styles.column, styles.column1)}>
            <IconMinus
              variant='neutralCool'
              variantCount='700'
              className={styles.icon}
            />
          </div>
          <div className={classnames(styles.column, styles.column2)}>
            <Text element='span' variant='neutralCool' variantCount='700'>
              <Format.Currency value={homeValue * AVG_SELLING_FEELS} />
            </Text>
          </div>
          <div className={classnames(styles.column, styles.column3)}>
            <Text variant='neutralCool' variantCount='700'>
              <FormattedMessage
                id='ShouldYouSell.cashPosition.bad.body.sellingFees'
                defaultMessage='Avg Selling fees ({sellingFees})'
                values={{
                  sellingFees: (
                    <Format.Percentage value={AVG_SELLING_FEELS * 100} />
                  ),
                }}
              />
            </Text>
          </div>
        </div>
        <hr className={styles.hr} />
        <div className={styles.row}>
          <div className={classnames(styles.column, styles.column1)} />
          <div className={classnames(styles.column, styles.column2)}>
            <Text element='span' variant={cashRemainingVariant}>
              <Format.Currency value={cashRemaining} />
            </Text>
          </div>
          <div className={classnames(styles.column, styles.column3)}>
            <Text variant={cashRemainingVariant} weight='semibold'>
              <If condition={cashRemaining <= 0}>
                <FormattedMessage
                  id='ShouldYouSell.cashPosition.bad.body.cashOwed'
                  defaultMessage='Cash owed'
                />
              </If>

              <Unless condition={cashRemaining <= 0}>
                <FormattedMessage
                  id='ShouldYouSell.cashPosition.bad.body.cashLeft'
                  defaultMessage='Cash remaining'
                />
              </Unless>
            </Text>
          </div>
        </div>
      </div>
      <If condition={recentSoldDate}>
        <div className={styles.soldDateDisclaimer}>
          <SoldDateDisclaimer />
        </div>
      </If>
      <Text
        element='div'
        variant='neutralCool'
        variantCount='900'
        className={styles.subHeading}
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.bad.body.subheader'
          defaultMessage='How much equity is enough?'
        />
      </Text>
      <Text
        element='p'
        size='xs'
        variant='neutralCool'
        variantCount='700'
        className={styles.paragraph}
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.bad.body.paragraphOne'
          defaultMessage='After selling your home, you ideally should have enough equity to pay off your current mortgage and some left over to provide a 20% down payment on your next home.'
        />
      </Text>
      <Text
        size='xs'
        element='p'
        variant='neutralCool'
        variantCount='700'
        className={styles.paragraph}
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.bad.body.paragraphTwo'
          defaultMessage='Putting down at least 20% saves you from paying Private Mortgage Insurance (PMI) -- which can save you hundreds, or in some cases, thousands of dollars over the life of your loan.'
        />
      </Text>
      <div className={styles.question}>
        <QuestionSingle
          questionTopic={intlFormat(QUESTIONS.cashPositionTopic)}
          questionTopicKey={HOMEOWNER_CASH_POSITION_TOPIC_KEY}
          placeholder={intlFormat(QUESTIONS.badCashPosition)}
        />
      </div>
    </div>
  )
}

export default Body
